import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useIsMobile } from 'hooks/useIsMobile';

interface SidebarState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarState | undefined>(undefined);

/**
 * Desktop: the sidebar starts open and keeps its state across routes.
 * Mobile: the sidebar is an overlay, so it starts closed and closes on every route change.
 */
export const SidebarProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(!isMobile);

  useEffect(() => setIsOpen(!isMobile), [isMobile]);

  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [pathname]);

  const open = useCallback((): void => setIsOpen(true), []);
  const close = useCallback((): void => setIsOpen(false), []);
  const toggle = useCallback((): void => setIsOpen((prev) => !prev), []);

  const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = (): SidebarState => {
  const context = useContext(SidebarContext);
  if (context === undefined) throw new Error('useSidebar must be used within SidebarProvider');
  return context;
};
